import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DemoExportGate } from "@/components/DemoExportGate";
import { Badge } from "@/components/ui/badge";
import {
  Download, FileText, FileSpreadsheet,
  DollarSign, UserCog, Scale, ShieldAlert, ScrollText, Scale3d,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useOrg, type Persona } from "@/lib/orgContext";
import { PersonaBadge } from "@/components/PersonaBadge";

async function triggerDownload(url: string, filename: string): Promise<void> {
  const res = await fetch(url);
  if (!res.ok) {
    if (res.status === 403) throw new Error("You don't have access to this report.");
    throw new Error("Download failed");
  }
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
}

const PERSONA_REPORTS: {
  persona: Persona;
  title: string;
  description: string;
  icon: React.ElementType;
  contents: string[];
  csvUrl: string;
  pdfUrl: string;
  color: string;
}[] = [
  {
    persona: "Finance",
    // Task #391: SaaS billing (plan / subscription / seats) intentionally
    // stripped from the staff Finance report — that's a super-admin
    // /admin concern, not athletic-department finance.
    title: "Finance Report",
    description: "Committed compensation, buyout exposure, and renewal cost across active contracts.",
    icon: DollarSign,
    contents: [
      "Executive KPIs: active contracts, annual base, committed remaining, buyout exposure",
      "Contracts broken down by status and by sport (count, annual base, committed remaining)",
      "Renewals by quarter for the next 3 years (contracts ending and annual base at risk)",
      "Top 10 buyout exposure list and full active-contract roster sorted by salary",
    ],
    csvUrl: "/api/reports/finance.csv",
    pdfUrl: "/api/reports/finance.pdf",
    color: "text-emerald-600 dark:text-emerald-400",
  },
  {
    persona: "HR",
    // Task #391: SaaS seat utilization stripped from the HR report — it's
    // not roster HR.
    title: "HR Report",
    description: "Coach roster, contract expirations, and headcount changes.",
    icon: UserCog,
    contents: [
      "Headcount by status (active / on-leave / inactive) and roster breakdown by sport",
      "Expiration buckets at 30 / 60 / 90 / 180 / 365 days",
      "Recent hires and recent departures (last 90 days)",
      "Coaches needing attention (no active contract, expiring soon, missing insurance, missing phone)",
      "Full coach roster with contract end date, days-to-expiration, and persona owner",
    ],
    csvUrl: "/api/reports/hr.csv",
    pdfUrl: "/api/reports/hr.pdf",
    color: "text-blue-600 dark:text-blue-400",
  },
  {
    persona: "Legal",
    title: "Legal Report",
    description: "Compliance posture, insurance gaps, negotiation activity, and audit summary.",
    icon: Scale,
    contents: [
      "Compliance failures grouped by severity (critical / high / medium / low) with open vs resolved",
      "Insurance gaps: active coaches with no policies on file or expired/expiring soon",
      "Negotiation activity: open items, rounds sent/received in 30 days, comments volume",
      "Clause coverage by category (seed vs custom) and recent override history (locked/unlocked)",
      "Audit summary: top actors with personas, top event types, and 30-day daily volume",
    ],
    csvUrl: "/api/reports/legal.csv",
    pdfUrl: "/api/reports/legal.pdf",
    color: "text-purple-600 dark:text-purple-400",
  },
];

interface ExtraReport {
  key: "termSheets" | "equity";
  title: string;
  description: string;
  icon: React.ElementType;
  contents: string[];
  csvUrl: string;
  pdfUrl: string;
  color: string;
  badgeLabel: string;
}

const EXTRA_REPORTS: ExtraReport[] = [
  {
    key: "termSheets",
    title: "Term Sheet Pipeline",
    description: "All open and closed term sheets routed for legal sign-off and counter-offers.",
    icon: ScrollText,
    contents: [
      "Counts by lifecycle bucket (draft, internally approved, sent, countered, accepted, declined)",
      "Coach, sport, title, salary range, signing bonus, and last-updated date for every term sheet",
      "Headline KPIs: in-flight, accepted, declined",
    ],
    csvUrl: "/api/reports/term-sheets.csv",
    pdfUrl: "/api/reports/term-sheets.pdf",
    color: "text-indigo-600 dark:text-indigo-400",
    badgeLabel: "Admin · Legal · HR",
  },
  {
    key: "equity",
    title: "Title IX Equity Report",
    description: "Men's vs women's coaching spend, participation, and per-sport disparities.",
    icon: Scale3d,
    contents: [
      "Spend & participation summary (annual base + signing + bonuses) split by program gender",
      "Spend ratio vs participation ratio with women's spend − participation gap (Title IX core test)",
      "Per-sport drill-down: men's/women's spend, coach counts, athlete counts, and the spend Δ",
    ],
    csvUrl: "/api/reports/equity.csv",
    pdfUrl: "/api/reports/equity.pdf",
    color: "text-pink-600 dark:text-pink-400",
    badgeLabel: "Admin · Finance",
  },
];

interface AvailableResponse {
  available: Persona[];
  extras?: { termSheets?: boolean; equity?: boolean };
}

export default function Reports() {
  const { org } = useOrg();
  const { toast } = useToast();
  const [available, setAvailable] = useState<Persona[] | null>(null);
  const [extras, setExtras] = useState<{ termSheets: boolean; equity: boolean }>({ termSheets: false, equity: false });
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/reports/available")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`status ${r.status}`))))
      .then((data: AvailableResponse) => {
        if (cancelled) return;
        setAvailable(data.available);
        setExtras({
          termSheets: Boolean(data.extras?.termSheets),
          equity: Boolean(data.extras?.equity),
        });
      })
      .catch(() => { if (!cancelled) setAvailable([]); });
    return () => { cancelled = true; };
  }, []);

  const isAdmin = org?.role === "admin";

  async function handleDownload(url: string, ext: "csv" | "pdf", key: string) {
    const dlKey = `${key}-${ext}`;
    setDownloading(dlKey);
    try {
      const date = new Date().toISOString().split("T")[0];
      await triggerDownload(url, `${key}-report-${date}.${ext}`);
    } catch (err) {
      toast({
        title: "Report download failed",
        description: err instanceof Error ? err.message : "Unable to download report.",
        variant: "destructive",
      });
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="page-header">
        <h1>Reports</h1>
        <p>Persona-curated CSV and PDF exports drawn from your existing data.</p>
      </div>

      {available !== null && available.length === 0 && !isAdmin && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            <ShieldAlert className="h-6 w-6 mx-auto mb-2 text-muted-foreground/60" />
            You don't have a persona that grants access to any report. Ask an admin to assign you Finance,
            HR, or Legal.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {PERSONA_REPORTS.map((r) => {
          const Icon = r.icon;
          const accessible = isAdmin || (available ?? []).includes(r.persona);
          return (
            <Card key={r.persona} className={accessible ? "" : "opacity-60"}>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${r.color}`} />
                  <span>{r.title}</span>
                  <PersonaBadge persona={r.persona} size="xs" />
                  {!accessible && (
                    <Badge variant="outline" className="ml-auto text-[10px]">
                      No access
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs">{r.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                  {r.contents.map((c) => <li key={c}>{c}</li>)}
                </ul>
                <div className="flex gap-2 pt-2">
                  <DemoExportGate label="Export" size="sm" variant="outline">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!accessible || downloading !== null}
                      onClick={() => handleDownload(r.csvUrl, "csv", r.persona.toLowerCase())}
                      data-testid={`download-${r.persona.toLowerCase()}-csv`}
                    >
                      <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
                      {downloading === `${r.persona.toLowerCase()}-csv` ? "Downloading…" : "CSV"}
                    </Button>
                    <Button
                      size="sm"
                      disabled={!accessible || downloading !== null}
                      onClick={() => handleDownload(r.pdfUrl, "pdf", r.persona.toLowerCase())}
                      data-testid={`download-${r.persona.toLowerCase()}-pdf`}
                    >
                      <FileText className="h-3.5 w-3.5 mr-1.5" />
                      {downloading === `${r.persona.toLowerCase()}-pdf` ? "Downloading…" : "PDF"}
                    </Button>
                  </DemoExportGate>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {EXTRA_REPORTS.map((r) => {
          const Icon = r.icon;
          const accessible = isAdmin || extras[r.key];
          return (
            <Card key={r.key} className={accessible ? "" : "opacity-60"}>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${r.color}`} />
                  <span>{r.title}</span>
                  <Badge variant="outline" className="text-[10px] font-normal">{r.badgeLabel}</Badge>
                  {!accessible && (
                    <Badge variant="outline" className="ml-auto text-[10px]">
                      No access
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs">{r.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                  {r.contents.map((c) => <li key={c}>{c}</li>)}
                </ul>
                <div className="flex gap-2 pt-2">
                  <DemoExportGate label="Export" size="sm" variant="outline">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!accessible || downloading !== null}
                      onClick={() => handleDownload(r.csvUrl, "csv", r.key)}
                      data-testid={`download-${r.key}-csv`}
                    >
                      <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
                      {downloading === `${r.key}-csv` ? "Downloading…" : "CSV"}
                    </Button>
                    <Button
                      size="sm"
                      disabled={!accessible || downloading !== null}
                      onClick={() => handleDownload(r.pdfUrl, "pdf", r.key)}
                      data-testid={`download-${r.key}-pdf`}
                    >
                      <FileText className="h-3.5 w-3.5 mr-1.5" />
                      {downloading === `${r.key}-pdf` ? "Downloading…" : "PDF"}
                    </Button>
                  </DemoExportGate>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Download className="h-4 w-4 text-muted-foreground" />
            About these reports
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-2">
          <p>
            Reports are generated on demand from your organization's current data. Each download
            is logged in the audit log. Multi-tenant isolation is enforced — you only see your
            organization's records.
          </p>
          <p>
            Access is granted to organization Admins automatically; persona-scoped reports require
            the matching persona on your member profile (Finance, HR, or Legal). The Term Sheet
            pipeline is open to Admin / Legal / HR; the Title IX equity report is open to Admin /
            Finance, mirroring the equity CSV export.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
